import React, { FC, useMemo } from 'react';

import { Money as MoneyModel } from 'api-client';
import { cn, formatAmount as formatAmountUtil } from 'utils';

import './Money.scoped.scss';

export enum MoneyMode {
  WRITE_OFF = 'money--write-off',
  PENALTY = 'money--penalty',
  ADDITIONAL = 'money--additional',
}

const THOUSAND = 1000;
const MILLION = 1000000;

type Props = {
  money: MoneyModel;
  mode?: MoneyMode | undefined;
  withPennies?: boolean;
  isPenniesBlack?: boolean;
  compact?: boolean;
};

const formatShortAmount = (rubles: number): string => {
  const divider = Math.abs(rubles) >= MILLION ? MILLION : THOUSAND;
  const unit = divider === MILLION ? 'млн' : 'тыс';
  const value = (rubles / divider).toFixed(1).replace(/\.0$/, '');
  return `${value.replace('.', ',')}\xa0${unit}`;
};

const Money: FC<Props> = ({
  money,
  mode,
  withPennies = true,
  isPenniesBlack = false,
  compact = false,
}) => {
  const rublesAmount = money.amount / 100;
  const isShort = compact && Math.abs(rublesAmount) >= THOUSAND;

  const formatAmount = useMemo(() => {
    return formatAmountUtil(rublesAmount);
  }, [money]);

  if (isShort) {
    return (
      <span className={cn('money', mode)}>
        <span className="money__rubles">{formatShortAmount(rublesAmount)}</span>
        <span className={isPenniesBlack ? 'money__pennies__black' : 'money__pennies'}>&nbsp;₽</span>
      </span>
    );
  }

  const [rubles, pennies] = formatAmount.split('.');
  return (
    <span className={cn('money', mode)}>
      <span className="money__rubles">{rubles}</span>
      {withPennies && (
        <span className={isPenniesBlack ? 'money__pennies__black' : 'money__pennies'}>
          ,{pennies}
        </span>
      )}
      <span className={isPenniesBlack ? 'money__pennies__black' : 'money__pennies'}>&nbsp;₽</span>
    </span>
  );
};

export default Money;
